import { useEffect, useState } from 'react';
import { api, type AuthUsersEditPayload, type AuthUsersResp } from '@/lib/api';

interface Props {
  user: AuthUsersResp | null;
  onSaved: () => void;
}

const ProfileEditor = ({ user, onSaved }: Props) => {
  const [form, setForm] = useState<AuthUsersEditPayload>({
    nickname: '',
    userSex: 3,
    userAge: '',
    userHead: '',
    remark: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!user) {
      return;
    }
    setForm({
      nickname: user.nickname || '',
      userSex: user.userSex || 3,
      userAge: user.userAge || '',
      userHead: user.userHead || '',
      remark: user.remark || ''
    });
  }, [user]);

  const updateField = <K extends keyof AuthUsersEditPayload>(key: K, value: AuthUsersEditPayload[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = () => {
    setMessage('');
    setError('');
    if (!form.nickname.trim()) {
      setError('请输入昵称');
      return;
    }
    if (!form.userAge) {
      setError('请选择生日');
      return;
    }

    setSaving(true);
    void api
      .updateCurrentUser({
        nickname: form.nickname.trim(),
        userSex: form.userSex,
        userAge: form.userAge,
        userHead: form.userHead?.trim(),
        remark: form.remark?.trim()
      })
      .then(() => {
        setMessage('个人资料已更新');
        onSaved();
      })
      .catch((e: Error) => {
        setError(e.message || '保存失败，请稍后重试');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7 shadow-[0_14px_35px_-28px_rgba(15,23,42,0.45)]">
      <header className="mb-5">
        <h3 className="text-xl font-bold text-slate-900">编辑资料</h3>
        <p className="text-sm text-slate-500 mt-1">保存后会实时刷新个人中心信息。</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-slate-500">昵称</span>
          <input
            value={form.nickname}
            onChange={(e) => updateField('nickname', e.target.value)}
            placeholder="请输入昵称"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
          />
        </label>

        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-slate-500">性别</span>
          <select
            value={form.userSex}
            onChange={(e) => updateField('userSex', Number(e.target.value))}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
          >
            <option value={1}>男</option>
            <option value={2}>女</option>
            <option value={3}>保密</option>
          </select>
        </label>

        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-slate-500">生日</span>
          <input
            type="date"
            value={form.userAge}
            onChange={(e) => updateField('userAge', e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
          />
        </label>

        <label className="space-y-1.5">
          <span className="text-xs font-semibold text-slate-500">头像地址</span>
          <input
            value={form.userHead || ''}
            onChange={(e) => updateField('userHead', e.target.value)}
            placeholder="https://example.com/avatar.png"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
          />
        </label>
      </div>

      <label className="block mt-4 space-y-1.5">
        <span className="text-xs font-semibold text-slate-500">备注</span>
        <textarea
          value={form.remark || ''}
          onChange={(e) => updateField('remark', e.target.value)}
          rows={4}
          placeholder="可填写学习目标或个人简介"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white resize-y"
        />
      </label>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}
      {message ? <p className="mt-3 text-sm text-emerald-600">{message}</p> : null}

      <div className="mt-5 flex items-center gap-3">
        <button
          disabled={saving}
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-save-line" />
          {saving ? '保存中...' : '保存资料'}
        </button>
      </div>
    </section>
  );
};

export default ProfileEditor;
